//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/*
  Dates in the interface language (DESIGN.md §7.3).

  Upstream screens format dates with the browser's default locale
  (`toLocaleString('default', …)`, `new Intl.DateTimeFormat([], …)`), so an
  Uzbek interface showed "Wed, September 16" and "1:27 PM". Here every
  default-locale call follows the interface language instead. Calls that
  name a locale (date-fns-tz uses 'en-US' to parse, for example) are left
  alone.

  Chrome's built-in Uzbek data is incomplete ("M09 16"), so Uzbek is
  formatted here: "16-sentabr", "Chorshanba, 16-sentabr 2026", "16.09.2026",
  "Sentabr 2026", 24-hour "13:27".

  CLDR's short Uzbek names read as other words or as typos: "chor" is "four",
  "iyn" and "iyl" are not words. A weekday next to a date is written in full
  ("Chorshanba, 16-sentabr"), on its own (calendar heads) in two letters
  ("Ch"); June and July keep their whole four letters ("16-iyun").
*/

type Options = Intl.DateTimeFormatOptions
type Part = Intl.DateTimeFormatPart
type Required = 'date' | 'time' | 'any'
type Defaults = 'date' | 'time' | 'all'

const MONTHS_LONG = [
  'yanvar',
  'fevral',
  'mart',
  'aprel',
  'may',
  'iyun',
  'iyul',
  'avgust',
  'sentabr',
  'oktabr',
  'noyabr',
  'dekabr'
]
const MONTHS_SHORT = ['yan', 'fev', 'mar', 'apr', 'may', 'iyun', 'iyul', 'avg', 'sen', 'okt', 'noy', 'dek']
const WEEKDAYS_LONG = ['yakshanba', 'dushanba', 'seshanba', 'chorshanba', 'payshanba', 'juma', 'shanba']
// Two letters, as Uzbek calendars print them (one letter would repeat S).
const WEEKDAYS_NARROW = ['Ya', 'Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh']
const EN_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const RealDateTimeFormat = Intl.DateTimeFormat
const realToLocaleString = Date.prototype.toLocaleString
const realToLocaleDateString = Date.prototype.toLocaleDateString
const realToLocaleTimeString = Date.prototype.toLocaleTimeString

let uiLanguage = ''

function isDefaultLocale (locales: unknown): boolean {
  return locales === undefined || locales === 'default' || (Array.isArray(locales) && locales.length === 0)
}

/** The locale a call should use; 'uz' means "format it here". */
function targetLocale (locales: any): any {
  if (!isDefaultLocale(locales)) return locales
  if (uiLanguage === '' || uiLanguage === 'en') return locales
  return uiLanguage
}

function capitalize (s: string): string {
  return s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s
}

function pad2 (n: number): string {
  return n < 10 ? `0${n}` : `${n}`
}

const fieldsCache = new Map<string, Intl.DateTimeFormat>()

interface Fields {
  year: number
  month: number
  day: number
  weekday: number
  hour: number
  minute: number
  second: number
  timeZoneName: string
}

/** The date's fields in the requested time zone. */
function getFields (date: Date, timeZone: string | undefined, timeZoneName: Options['timeZoneName']): Fields {
  const key = `${timeZone ?? ''}|${timeZoneName ?? ''}`
  let dtf = fieldsCache.get(key)
  if (dtf === undefined) {
    dtf = new RealDateTimeFormat('en-US', {
      timeZone,
      timeZoneName,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
    fieldsCache.set(key, dtf)
  }
  const out: Fields = { year: 0, month: 1, day: 1, weekday: 0, hour: 0, minute: 0, second: 0, timeZoneName: '' }
  for (const p of dtf.formatToParts(date)) {
    switch (p.type) {
      case 'year':
        out.year = parseInt(p.value, 10)
        break
      case 'month':
        out.month = parseInt(p.value, 10)
        break
      case 'day':
        out.day = parseInt(p.value, 10)
        break
      case 'weekday':
        out.weekday = Math.max(0, EN_WEEKDAYS.indexOf(p.value))
        break
      case 'hour':
        out.hour = parseInt(p.value, 10) % 24
        break
      case 'minute':
        out.minute = parseInt(p.value, 10)
        break
      case 'second':
        out.second = parseInt(p.value, 10)
        break
      case 'timeZoneName':
        out.timeZoneName = p.value
        break
    }
  }
  return out
}

/** dateStyle/timeStyle as fields, and the defaults JS adds when no field is asked for. */
function resolveOptions (options: Options | undefined, required: Required, defaults: Defaults): Options {
  const o: Options = { ...(options ?? {}) }
  if (o.dateStyle !== undefined) {
    const style = o.dateStyle
    if (style === 'full') o.weekday = 'long'
    o.year = 'numeric'
    o.day = style === 'short' ? '2-digit' : 'numeric'
    o.month = style === 'full' || style === 'long' ? 'long' : style === 'medium' ? 'short' : '2-digit'
    delete o.dateStyle
  }
  if (o.timeStyle !== undefined) {
    o.hour = '2-digit'
    o.minute = '2-digit'
    if (o.timeStyle !== 'short') o.second = '2-digit'
    delete o.timeStyle
  }
  const hasDate = o.weekday !== undefined || o.year !== undefined || o.month !== undefined || o.day !== undefined
  const hasTime = o.hour !== undefined || o.minute !== undefined || o.second !== undefined
  let needDefaults = true
  if (required === 'date' && hasDate) needDefaults = false
  if (required === 'time' && hasTime) needDefaults = false
  if (required === 'any' && (hasDate || hasTime)) needDefaults = false
  if (needDefaults) {
    if (defaults === 'date' || defaults === 'all') {
      o.year = 'numeric'
      o.month = '2-digit'
      o.day = '2-digit'
    }
    if (defaults === 'time' || defaults === 'all') {
      o.hour = '2-digit'
      o.minute = '2-digit'
      o.second = '2-digit'
    }
  }
  return o
}

function uzParts (date: Date, options: Options | undefined, required: Required, defaults: Defaults): Part[] {
  const o = resolveOptions(options, required, defaults)
  const f = getFields(date, o.timeZone, o.timeZoneName)
  const parts: Part[] = []
  const lit = (value: string): void => {
    parts.push({ type: 'literal', value })
  }
  const year = o.year === '2-digit' ? pad2(f.year % 100) : `${f.year}`
  const textMonth = o.month === 'long' || o.month === 'short' || o.month === 'narrow'

  // Date: "16-sentabr 2026", "16.09.2026", "Sentabr 2026".
  const dateParts: Part[] = []
  if (textMonth) {
    const name = (o.month === 'long' ? MONTHS_LONG : MONTHS_SHORT)[f.month - 1]
    if (o.day !== undefined) {
      dateParts.push({ type: 'day', value: `${f.day}` }, { type: 'literal', value: '-' }, { type: 'month', value: name })
    } else {
      dateParts.push({ type: 'month', value: capitalize(name) })
    }
    if (o.year !== undefined) dateParts.push({ type: 'literal', value: ' ' }, { type: 'year', value: year })
  } else if (o.month !== undefined) {
    if (o.day !== undefined) {
      dateParts.push({ type: 'day', value: pad2(f.day) }, { type: 'literal', value: '.' })
    }
    dateParts.push({ type: 'month', value: pad2(f.month) })
    if (o.year !== undefined) dateParts.push({ type: 'literal', value: '.' }, { type: 'year', value: year })
  } else {
    if (o.day !== undefined) dateParts.push({ type: 'day', value: o.day === '2-digit' ? pad2(f.day) : `${f.day}` })
    if (o.year !== undefined) {
      if (dateParts.length > 0) dateParts.push({ type: 'literal', value: ' ' })
      dateParts.push({ type: 'year', value: year })
    }
  }

  if (o.weekday !== undefined) {
    const full = o.weekday === 'long' || (o.weekday === 'short' && dateParts.length > 0)
    const names = full ? WEEKDAYS_LONG : WEEKDAYS_NARROW
    parts.push({ type: 'weekday', value: capitalize(names[f.weekday]) })
    if (dateParts.length > 0) lit(', ')
  }
  parts.push(...dateParts)

  // Time: always 24 h (DESIGN.md §7.3).
  const timeParts: Part[] = []
  if (o.hour !== undefined) {
    timeParts.push({ type: 'hour', value: o.minute !== undefined || o.hour === '2-digit' ? pad2(f.hour) : `${f.hour}` })
  }
  if (o.minute !== undefined) {
    if (timeParts.length > 0) timeParts.push({ type: 'literal', value: ':' })
    timeParts.push({ type: 'minute', value: pad2(f.minute) })
  }
  if (o.second !== undefined) {
    if (timeParts.length > 0) timeParts.push({ type: 'literal', value: ':' })
    timeParts.push({ type: 'second', value: pad2(f.second) })
  }
  if (timeParts.length > 0) {
    if (parts.length > 0) lit(', ')
    parts.push(...timeParts)
  }
  if (o.timeZoneName !== undefined && f.timeZoneName !== '') {
    if (parts.length > 0) lit(' ')
    parts.push({ type: 'timeZoneName', value: f.timeZoneName })
  }
  return parts
}

function uzFormat (date: Date, options: Options | undefined, required: Required, defaults: Defaults): string {
  return uzParts(date, options, required, defaults)
    .map((p) => p.value)
    .join('')
}

function toDate (value: Date | number | undefined): Date {
  return value === undefined ? new Date() : value instanceof Date ? value : new Date(value)
}

/** Stands in for Intl.DateTimeFormat when the interface is Uzbek. */
class UzDateTimeFormat {
  private readonly base: Intl.DateTimeFormat

  constructor (private readonly options: Options | undefined) {
    // Resolved options (time zone, 24 h) come from a real 24 h locale.
    this.base = new RealDateTimeFormat('en-GB', options)
  }

  format = (value?: Date | number): string => {
    const d = toDate(value)
    if (isNaN(d.getTime())) return this.base.format(d)
    return uzFormat(d, this.options, 'any', 'date')
  }

  formatToParts (value?: Date | number): Part[] {
    const d = toDate(value)
    if (isNaN(d.getTime())) return this.base.formatToParts(d)
    return uzParts(d, this.options, 'any', 'date')
  }

  formatRange (start: Date | number, end: Date | number): string {
    return `${this.format(start)} – ${this.format(end)}`
  }

  formatRangeToParts (start: Date | number, end: Date | number): Part[] {
    return [...this.formatToParts(start), { type: 'literal', value: ' – ' }, ...this.formatToParts(end)]
  }

  resolvedOptions (): Intl.ResolvedDateTimeFormatOptions {
    return { ...this.base.resolvedOptions(), locale: 'uz-UZ' }
  }
}

let installed = false

function install (): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  function PatchedDateTimeFormat (locales?: any, options?: Options): Intl.DateTimeFormat {
    const locale = targetLocale(locales)
    if (locale === 'uz') return new UzDateTimeFormat(options) as unknown as Intl.DateTimeFormat
    return new RealDateTimeFormat(locale, options)
  }
  const patched: any = PatchedDateTimeFormat
  patched.prototype = RealDateTimeFormat.prototype
  patched.supportedLocalesOf = RealDateTimeFormat.supportedLocalesOf.bind(RealDateTimeFormat)
  ;(Intl as any).DateTimeFormat = patched

  const wrap = (
    real: (this: Date, locales?: any, options?: Options) => string,
    required: Required,
    defaults: Defaults
  ): ((this: Date, locales?: any, options?: Options) => string) =>
    function (this: Date, locales?: any, options?: Options): string {
      const locale = targetLocale(locales)
      if (locale !== 'uz' || isNaN(this.getTime())) return real.call(this, locale, options)
      try {
        return uzFormat(this, options, required, defaults)
      } catch (err: any) {
        return real.call(this, 'en-GB', options)
      }
    }
  // eslint-disable-next-line no-extend-native
  Date.prototype.toLocaleString = wrap(realToLocaleString, 'any', 'all')
  // eslint-disable-next-line no-extend-native
  Date.prototype.toLocaleDateString = wrap(realToLocaleDateString, 'date', 'date')
  // eslint-disable-next-line no-extend-native
  Date.prototype.toLocaleTimeString = wrap(realToLocaleTimeString, 'time', 'time')
}

/**
 * Default-locale date formatting follows this language from now on.
 * @public
 */
export function setDateLanguage (language: string): void {
  install()
  uiLanguage = language
}
